/**
 * DEVLOGS-TEMP — WebSocket frame capture, both directions.
 *
 * IMPLEMENTED AS `class extends`, AND THAT IS LOAD-BEARING.
 * artifacts/web/src/player/hooks/use-player-realtime.tsx reads
 * `WebSocket.CONNECTING` and `WebSocket.OPEN` off the global, and the native
 * hook does the same. A Proxy or a plain function wrapper breaks `instanceof`
 * and the static constants; the reconnect loop then fails SILENTLY — the kiosk
 * shows "Live" while every operator command vanishes. Do not "simplify" this.
 *
 * THE SUBPROTOCOL IS THE CREDENTIAL. Sockets are opened as
 * `["device.<screenCredential>"]` or `["bearer.<clerkToken>"]`. Only the prefix
 * is ever recorded, and it selects the namespace so one predicate separates
 * player sockets from control sockets.
 */

import { LEVELS } from "../level.ts";
import { FrameCoalescer } from "../coalesce.ts";
import { sanitizePayload, sanitizeUrl, scrubString } from "../redact.ts";
import type { EmitFn } from "../types.ts";

export interface WsCaptureOptions {
  emit: EmitFn;
  now: () => number;
  /** Frame types whose (sanitized) body is worth keeping. */
  bodyFrameTypes?: readonly string[];
  maxBodyBytes?: number;
  binaryWindowMs?: number;
}

/**
 * Control-plane frames worth a body. Deliberately excludes anything that could
 * carry audio or a large payload.
 */
const DEFAULT_BODY_TYPES: readonly string[] = [
  "welcome",
  "ack",
  "error",
  "command",
  "command-result",
  "device-metric",
  "schedule-updated",
  "device-updated",
  "client-advisory",
  "credential-revoked",
  "cache-report",
  "device-cache",
  "broadcast-ended",
  "announcement-started",
  "announcement-ended",
  "heartbeat",
  "status",
  "now-playing",
];

/**
 * Periodic streams where each frame says substantially what the last one said.
 * These are sampled per (socket, direction, type): a frame is recorded when its
 * FINGERPRINT changes (state/item transitions always log) or the last recorded
 * one is older than the window. Mirrors the server tap's policy
 * (api-server realtime/frame-log.ts).
 *
 * WHY. Client-side ws capture is the pipeline's biggest volume driver: a
 * console receives every fan-out now-playing from every device (O(consoles x
 * devices) at a 2-10s report rate) — measured at ~55k rows/day per surface,
 * against the Worker's documented ~860k/day budget. Commands, errors and
 * signalling stay unsampled: they are rare and they are what investigations
 * actually read.
 */
const CHATTY_FRAME_TYPES: ReadonlySet<string> = new Set([
  "heartbeat",
  "now-playing",
  "device-status",
  "ping",
  "pong",
]);
// DELIBERATELY NOT in the set, unlike the server tap's list:
//  - "ack": client-side it carries command outcomes, and its fingerprint
//    fields (of/commandId/delivered) are not periodic — a delivered:false ack
//    within 5 minutes of a delivered:true one is exactly the evidence an
//    investigation reads, and sampling dropped it (caught in review).
//  - "presence": broadcast only on connect/close — pure roster transitions.
//  - "device-metric": rare, event-driven, and the payload is the point.
const CHATTY_SAMPLE_WINDOW_MS = 5 * 60_000;

/** Stable identity of a chatty frame: transitions must always be recorded. */
function chattyFingerprint(type: string, obj: Record<string, unknown> | null): string {
  if (!obj) return type;
  const state = typeof obj["state"] === "string" ? obj["state"] : "";
  const item = typeof obj["itemId"] === "string" ? obj["itemId"] : "";
  const device = typeof obj["deviceId"] === "string" ? obj["deviceId"] : "";
  const status = typeof obj["status"] === "string" ? obj["status"] : "";
  return `${type}|${device}|${state}|${item}|${status}`;
}

const PATCH_TAG = Symbol.for("omnimedia.devlog.ws");

export function installWebSocketCapture(
  scope: { WebSocket: typeof WebSocket },
  options: WsCaptureOptions,
): () => void {
  const Original = scope.WebSocket as typeof WebSocket & { [PATCH_TAG]?: boolean };
  if (typeof Original !== "function" || Original[PATCH_TAG]) return () => {};

  const bodyTypes = new Set(options.bodyFrameTypes ?? DEFAULT_BODY_TYPES);
  const maxBodyBytes = options.maxBodyBytes ?? 2048;
  const binaryWindowMs = options.binaryWindowMs ?? 1000;

  class DevLogWebSocket extends Original {
    readonly #ns: string;
    readonly #openedAt: number;
    readonly #inFrames: FrameCoalescer;
    readonly #outFrames: FrameCoalescer;

    constructor(url: string | URL, protocols?: string | string[]) {
      super(url, protocols);
      const kind = protocolKind(protocols);
      this.#ns = kind === "device" ? "ws.device" : kind === "bearer" ? "ws.bearer" : "ws";
      this.#openedAt = safeCall(options.now, 0);
      this.#inFrames = new FrameCoalescer(binaryWindowMs, options.now);
      this.#outFrames = new FrameCoalescer(binaryWindowMs, options.now);

      safeVoid(() =>
        options.emit(LEVELS.info, this.#ns, "connecting", {
          url: sanitizeUrl(String(url)),
          protocolKind: kind,
        }),
      );

      safeVoid(() => {
        this.addEventListener("open", () => {
          safeVoid(() =>
            options.emit(LEVELS.info, this.#ns, "open", undefined, {
              dur: safeCall(options.now, 0) - this.#openedAt,
            }),
          );
        });
        this.addEventListener("close", (event) => {
          const e = event as unknown as { code?: number; reason?: string; wasClean?: boolean };
          safeVoid(() => {
            this.#flushFrames();
            options.emit(
              LEVELS.warn,
              this.#ns,
              "close",
              {
                code: e.code,
                wasClean: e.wasClean,
                reason: e.reason ? scrubString(e.reason, 120) : undefined,
              },
              { dur: safeCall(options.now, 0) - this.#openedAt },
            );
          });
        });
        this.addEventListener("error", () => {
          safeVoid(() => options.emit(LEVELS.error, this.#ns, "error"));
        });
        this.addEventListener("message", (event) => {
          const e = event as unknown as { data?: unknown };
          this.#record("recv", e.data, this.#inFrames);
        });
      });
    }

    override send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
      this.#record("send", data, this.#outFrames);
      super.send(data);
    }

    #flushFrames(): void {
      const inbound = this.#inFrames.flush();
      if (inbound) {
        safeVoid(() => options.emit(LEVELS.debug, this.#ns, "recv.binary", { ...inbound }));
      }
      const outbound = this.#outFrames.flush();
      if (outbound) {
        safeVoid(() => options.emit(LEVELS.debug, this.#ns, "send.binary", { ...outbound }));
      }
    }

    /** last-recorded marker per (direction, type) for the chatty sampler. */
    readonly #chattySeen = new Map<string, { at: number; fp: string }>();

    #shouldRecordChatty(direction: string, type: string, obj: Record<string, unknown> | null): boolean {
      if (!CHATTY_FRAME_TYPES.has(type)) return true;
      const key = `${direction}:${type}:${typeof obj?.["deviceId"] === "string" ? obj["deviceId"] : ""}`;
      const fp = chattyFingerprint(type, obj);
      const nowMs = Date.now();
      const prev = this.#chattySeen.get(key);
      if (prev && prev.fp === fp && nowMs - prev.at < CHATTY_SAMPLE_WINDOW_MS) {
        return false;
      }
      this.#chattySeen.set(key, { at: nowMs, fp });
      return true;
    }

    #record(direction: "send" | "recv", data: unknown, frames: FrameCoalescer): void {
      safeVoid(() => {
        if (typeof data !== "string") {
          // Binary: counts only. A live broadcast pushes audio frames, and
          // buffering them would be both a memory and a privacy failure.
          const bytes = byteLength(data);
          const summary = frames.add(bytes);
          if (summary) {
            options.emit(LEVELS.debug, this.#ns, `${direction}.binary`, { ...summary });
          }
          return;
        }

        const bytes = data.length;
        let parsed: unknown;
        try {
          parsed = JSON.parse(data);
        } catch {
          options.emit(LEVELS.debug, this.#ns, direction, {
            kind: "text",
            bytes,
            head: scrubString(data.slice(0, 64), 64),
          });
          return;
        }

        const obj = parsed as Record<string, unknown> | null;
        const type = obj && typeof obj["type"] === "string" ? (obj["type"] as string) : "unknown";
        if (!this.#shouldRecordChatty(direction, type, obj)) return;
        const payload: Record<string, unknown> = {
          type,
          bytes,
          keys: obj ? Object.keys(obj).slice(0, 24) : undefined,
        };
        if (bodyTypes.has(type) && bytes <= maxBodyBytes) {
          payload["frame"] = sanitizePayload(obj, { maxBytes: maxBodyBytes });
        }
        options.emit(LEVELS.debug, this.#ns, direction, payload, { msg: type });
      });
    }
  }

  // Statics (OPEN/CONNECTING/CLOSING/CLOSED) come along via prototype chain on
  // a class extends, but copy own enumerable statics defensively for hosts that
  // define them as own properties.
  safeVoid(() => {
    for (const key of Object.getOwnPropertyNames(Original)) {
      if (["length", "name", "prototype"].includes(key)) continue;
      const descriptor = Object.getOwnPropertyDescriptor(Original, key);
      if (descriptor && !(key in DevLogWebSocket)) {
        Object.defineProperty(DevLogWebSocket, key, descriptor);
      }
    }
  });

  (DevLogWebSocket as unknown as { [PATCH_TAG]?: boolean })[PATCH_TAG] = true;
  scope.WebSocket = DevLogWebSocket as unknown as typeof WebSocket;

  return () => {
    if (scope.WebSocket === (DevLogWebSocket as unknown as typeof WebSocket)) {
      scope.WebSocket = Original;
    }
  };
}

function protocolKind(
  protocols?: string | string[],
): "device" | "bearer" | "none" {
  const list = typeof protocols === "string" ? [protocols] : (protocols ?? []);
  for (const entry of list) {
    if (typeof entry !== "string") continue;
    if (entry.startsWith("device.")) return "device";
    if (entry.startsWith("bearer.")) return "bearer";
  }
  return "none";
}

function byteLength(data: unknown): number {
  try {
    if (data instanceof ArrayBuffer) return data.byteLength;
    if (ArrayBuffer.isView(data)) return data.byteLength;
    const blob = data as { size?: unknown };
    if (typeof blob?.size === "number") return blob.size;
  } catch {
    /* fall through */
  }
  return 0;
}

function safeVoid(fn: () => void): void {
  try {
    fn();
  } catch {
    /* fire-and-forget */
  }
}

function safeCall<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}
